import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { CalendarDays, Contact, ExternalLink, Info, MessageCircle, Quote, Lightbulb, Link2, ListChecks, Pencil, Pin, PinOff, Plus, StickyNote, Trash2, X } from 'lucide-react';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import type { ListItemRec, NoteRec } from '../domain/model';
import {
  addListItems,
  allTags,
  createNote,
  deleteListItems,
  deleteNote,
  itemsOf,
  parseTags,
  restoreListItems,
  restoreNote,
  searchNotes,
  setItemDone,
  updateNote,
  type NoteKind,
} from '../domain/notes';
import { Empty } from '../ui/Empty';
import { Field, Segmented, Switch } from '../ui/Field';
import { Sheet } from '../ui/Sheet';
import { useToast } from '../ui/Toast';
import { JournalPanel } from './JournalPanel';

type Tab = 'notes' | 'lists' | 'journal';
const NOTE_KIND_ICON = { note: StickyNote, idea: Lightbulb, link: Link2, date: CalendarDays, list: ListChecks, quote: Quote, fact: Info, contact: Contact } as const;
/** Categorías del Cerebro, en el orden de los filtros y del alta rápida. */
const CATEGORIES = ['note', 'idea', 'fact', 'quote', 'contact', 'link', 'date'] as const;

/** Cerebro: notas, ideas, enlaces, fechas importantes, listas y diario. */
export function BrainScreen() {
  const { t } = useI18n();
  // La pestaña vive en la URL (?tab=), así los accesos del Chat y de Ajustes abren la correcta.
  // El estado local responde al instante; la URL se actualiza después (la navegación es una transición).
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const parse = (v: string | null): Tab => (v === 'lists' || v === 'journal' ? v : 'notes');
  const [tab, setTabState] = useState<Tab>(() => parse(requested));
  const [seen, setSeen] = useState(requested);
  if (seen !== requested) {
    setSeen(requested);
    setTabState(parse(requested));
  }
  const setTab = (v: Tab) => {
    setTabState(v);
    setParams({ tab: v }, { replace: true });
  };
  return (
    <div className="stack-l">
      <header className="page-header" style={{ marginBottom: 0 }}>
        <h1>{t('brain.title')}</h1>
        <Link to="/chat" className="btn btn--ghost btn--sm">
          <MessageCircle size={16} aria-hidden="true" /> {t('brain.backToChat')}
        </Link>
      </header>
      <Segmented
        legend={t('brain.title')}
        value={tab}
        onChange={setTab}
        options={[
          { value: 'notes', label: t('brain.tabs.notes') },
          { value: 'lists', label: t('brain.tabs.lists') },
          { value: 'journal', label: t('brain.tabs.journal') },
        ]}
      />
      {tab === 'notes' && <NotesPanel />}
      {tab === 'lists' && <ListsPanel />}
      {tab === 'journal' && <JournalPanel />}
    </div>
  );
}

function NotesPanel() {
  const { t, fmtDate } = useI18n();
  const { data } = useApp();
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<'notes' | NoteKind>('notes');
  const [tag, setTag] = useState<string | null>(null);
  const [draftKind, setDraftKind] = useState<Exclude<NoteKind, 'list'>>('note');
  const [title, setTitle] = useState('');
  const [extra, setExtra] = useState('');
  const [editing, setEditing] = useState<NoteRec | null>(null);

  const notes = data.notes.filter((n) => n.kind !== 'list');
  const visible = searchNotes(notes, { query, kind, tag }, data.listItems);
  const tags = allTags(notes);
  const counts = Object.fromEntries(CATEGORIES.map((k) => [k, notes.filter((n) => n.kind === k).length])) as Record<(typeof CATEGORIES)[number], number>;

  const add = async () => {
    const text = title.trim();
    if (!text) return;
    // Pegar un enlace basta: el título se toma del dominio si no se escribe otro.
    const looksUrl = /^https?:\/\//i.test(text);
    const k: NoteKind = looksUrl ? 'link' : draftKind;
    try {
      await createNote({
        kind: k,
        title: looksUrl ? new URL(text).hostname : text,
        url: k === 'link' ? (looksUrl ? text : extra.trim() || null) : null,
        date: k === 'date' ? extra || null : null,
        body: k === 'quote' || k === 'contact' ? extra.trim() || null : null,
      });
      setTitle('');
      setExtra('');
      toast.show(t('brain.saved'));
    } catch {
      toast.show(t('brain.invalid'));
    }
  };

  const remove = async (n: NoteRec) => {
    await deleteNote(n.id);
    setEditing(null);
    toast.show(t('brain.deleted'), { label: t('common.undo'), run: () => void restoreNote(n.id) });
  };

  return (
    <div className="stack">
      <form
        className="card stack"
        aria-label={t('brain.quickAdd')}
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <Field label={t('brain.kindLabel')}>
            {(p) => (
              <select {...p} className="select" value={draftKind} onChange={(e) => setDraftKind(e.target.value as Exclude<NoteKind, 'list'>)}>
                {CATEGORIES.map((k) => (
                  <option key={k} value={k}>
                    {t(`brain.kinds.${k}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Field label={t('brain.titleLabel')} help={t('brain.quickHelp')}>
              {(p) => <input {...p} className="input" value={title} maxLength={500} onChange={(e) => setTitle(e.target.value)} />}
            </Field>
          </div>
        </div>
        {draftKind === 'link' && !/^https?:\/\//i.test(title) && (
          <Field label={t('brain.url')}>{(p) => <input {...p} type="url" className="input" value={extra} placeholder="https://" onChange={(e) => setExtra(e.target.value)} />}</Field>
        )}
        {draftKind === 'date' && <Field label={t('brain.date')}>{(p) => <input {...p} type="date" className="input" value={extra} onChange={(e) => setExtra(e.target.value)} />}</Field>}
        {draftKind === 'quote' && <Field label={`${t('brain.author')} (${t('common.optional')})`}>{(p) => <input {...p} className="input" value={extra} maxLength={120} onChange={(e) => setExtra(e.target.value)} />}</Field>}
        {draftKind === 'contact' && (
          <Field label={`${t('brain.details')} (${t('common.optional')})`} help={t('brain.contactHelp')}>
            {(p) => <input {...p} className="input" value={extra} maxLength={500} onChange={(e) => setExtra(e.target.value)} />}
          </Field>
        )}
        <button type="submit" className="btn btn--primary" disabled={!title.trim()}>
          <Plus size={18} aria-hidden="true" /> {t('brain.add')}
        </button>
      </form>

      <Field label={t('brain.search')}>{(p) => <input {...p} type="search" className="input" value={query} onChange={(e) => setQuery(e.target.value)} />}</Field>
      <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }} role="group" aria-label={t('brain.filters')}>
        {(['notes', ...CATEGORIES] as const)
          .filter((k) => k === 'notes' || k === kind || counts[k] > 0)
          .map((k) => (
            <button key={k} type="button" className="chip-toggle brain__chip" aria-pressed={kind === k} onClick={() => setKind(k)}>
              {k === 'notes' ? t('brain.all') : t(`brain.kinds.${k}`)} <span className="brain__count num">{k === 'notes' ? notes.length : counts[k]}</span>
            </button>
          ))}
        {tags.map((g) => (
          <button key={g} type="button" className="chip-toggle" aria-pressed={tag === g} onClick={() => setTag(tag === g ? null : g)}>
            #{g}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <Empty title={notes.length === 0 ? t('brain.emptyTitle') : t('brain.noResults')} body={notes.length === 0 ? t('brain.emptyBody') : undefined} />
      ) : (
        <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }} aria-label={t('brain.tabs.notes')}>
          {visible.map((n) => {
            const Icon = NOTE_KIND_ICON[n.kind];
            return (
              <li key={n.id}>
                <article className="card stack" aria-label={n.title}>
                  <div className="row" style={{ alignItems: 'flex-start' }}>
                    <Icon size={20} aria-hidden="true" style={{ flex: 'none', marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ overflowWrap: 'anywhere' }}>{n.title}</strong>
                      <div className="muted xsmall">
                        {t(`brain.kinds.${n.kind}`)}
                        {n.date ? ` · ${fmtDate(n.date, { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={n.pinned ? t('brain.unpinNamed', { title: n.title }) : t('brain.pinNamed', { title: n.title })}
                      aria-pressed={n.pinned}
                      onClick={() => void updateNote(n.id, { pinned: !n.pinned })}
                    >
                      {n.pinned ? <PinOff size={18} aria-hidden="true" /> : <Pin size={18} aria-hidden="true" />}
                    </button>
                    <button type="button" className="icon-btn" aria-label={t('brain.editNamed', { title: n.title })} onClick={() => setEditing(n)}>
                      <Pencil size={18} aria-hidden="true" />
                    </button>
                  </div>
                  {n.body && <p className="small" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: 0 }}>{n.body}</p>}
                  {n.url && (
                    <a href={n.url} target="_blank" rel="noopener noreferrer" className="small" style={{ overflowWrap: 'anywhere' }}>
                      <ExternalLink size={14} aria-hidden="true" /> {n.url}
                    </a>
                  )}
                  {n.tags.length > 0 && <div className="muted xsmall">{n.tags.map((g) => `#${g}`).join(' ')}</div>}
                </article>
              </li>
            );
          })}
        </ul>
      )}

      <NoteEditor note={editing} onClose={() => setEditing(null)} onDelete={remove} />
    </div>
  );
}

function NoteEditor({ note, onClose, onDelete }: { note: NoteRec | null; onClose: () => void; onDelete: (n: NoteRec) => void }) {
  const { t } = useI18n();
  const { data } = useApp();
  const toast = useToast();
  return (
    <Sheet open={note !== null} onClose={onClose} title={t('brain.edit')}>
      {note && <NoteForm key={note.id} note={note} habits={data.habits.filter((h) => !h.deletedAt)} onDone={onClose} onDelete={onDelete} onError={() => toast.show(t('brain.invalid'))} />}
    </Sheet>
  );
}

function NoteForm({
  note,
  habits,
  onDone,
  onDelete,
  onError,
}: {
  note: NoteRec;
  habits: { id: string; name: string }[];
  onDone: () => void;
  onDelete: (n: NoteRec) => void;
  onError: () => void;
}) {
  const { t } = useI18n();
  const [title, setTitle] = useState(note.title);
  const [body, setBody] = useState(note.body ?? '');
  const [url, setUrl] = useState(note.url ?? '');
  const [date, setDate] = useState(note.date ?? '');
  const [tags, setTags] = useState(note.tags.join(', '));
  const [linked, setLinked] = useState<string[]>(note.habitIds);
  const [pinned, setPinned] = useState(note.pinned);

  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          await updateNote(note.id, {
            title: title.trim(),
            body: body.trim() || null,
            url: note.kind === 'link' ? url.trim() || null : null,
            date: note.kind === 'date' ? date || null : null,
            tags: parseTags(tags),
            habitIds: linked,
            pinned,
          });
          onDone();
        } catch {
          onError();
        }
      }}
    >
      <Field label={t('brain.titleLabel')}>{(p) => <input {...p} className="input" required maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />}</Field>
      {note.kind === 'link' && <Field label={t('brain.url')}>{(p) => <input {...p} type="url" className="input" value={url} onChange={(e) => setUrl(e.target.value)} />}</Field>}
      {note.kind === 'date' && <Field label={t('brain.date')}>{(p) => <input {...p} type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />}</Field>}
      <Field label={t('brain.body')}>{(p) => <textarea {...p} className="input" rows={5} maxLength={20_000} value={body} onChange={(e) => setBody(e.target.value)} />}</Field>
      <Field label={t('brain.tags')} help={t('brain.tagsHelp')}>
        {(p) => <input {...p} className="input" value={tags} onChange={(e) => setTags(e.target.value)} />}
      </Field>
      {habits.length > 0 && (
        <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="field__label">{t('brain.linkedHabits')}</legend>
          {habits.slice(0, 50).map((h) => (
            <label key={h.id} className="row small">
              <input
                type="checkbox"
                checked={linked.includes(h.id)}
                onChange={(e) => setLinked(e.target.checked ? [...linked, h.id].slice(0, 20) : linked.filter((x) => x !== h.id))}
              />
              {h.name}
            </label>
          ))}
        </fieldset>
      )}
      <Switch label={t('brain.pinned')} checked={pinned} onChange={setPinned} />
      <button type="submit" className="btn btn--primary btn--block">
        {t('common.save')}
      </button>
      <button type="button" className="btn btn--danger btn--block" onClick={() => onDelete(note)}>
        <Trash2 size={18} aria-hidden="true" /> {t('brain.delete')}
      </button>
    </form>
  );
}

function ListsPanel() {
  const { t } = useI18n();
  const { data } = useApp();
  const toast = useToast();
  const [name, setName] = useState('');
  const lists = searchNotes(data.notes, { kind: 'list' });

  return (
    <div className="stack">
      <form
        className="row"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return;
          await createNote({ kind: 'list', title: name });
          setName('');
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label={t('brain.newList')}>{(p) => <input {...p} className="input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
        </div>
        <button type="submit" className="btn btn--primary" style={{ alignSelf: 'flex-end' }} disabled={!name.trim()}>
          <Plus size={18} aria-hidden="true" /> {t('brain.add')}
        </button>
      </form>
      {lists.length === 0 ? (
        <Empty title={t('brain.listsEmptyTitle')} body={t('brain.listsEmptyBody')} />
      ) : (
        lists.map((l) => (
          <ListCard
            key={l.id}
            list={l}
            items={itemsOf(data.listItems, l.id)}
            all={data.listItems}
            onDelete={async () => {
              await deleteNote(l.id);
              toast.show(t('brain.deleted'), { label: t('common.undo'), run: () => void restoreNote(l.id) });
            }}
          />
        ))
      )}
    </div>
  );
}

function ListCard({ list, items, all, onDelete }: { list: NoteRec; items: ListItemRec[]; all: ListItemRec[]; onDelete: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [text, setText] = useState('');
  const done = items.filter((i) => i.done);

  const removeItems = async (ids: string[]) => {
    await deleteListItems(ids);
    toast.show(t('brain.itemsRemoved', { count: ids.length }), { label: t('common.undo'), run: () => void restoreListItems(ids) });
  };

  return (
    <section className="card stack" aria-label={list.title}>
      <div className="row">
        <ListChecks size={20} aria-hidden="true" />
        <h2 style={{ flex: 1, margin: 0, overflowWrap: 'anywhere' }}>{list.title}</h2>
        <span className="muted small">{t('brain.listProgress', { done: done.length, total: items.length })}</span>
        <button type="button" className="icon-btn" aria-label={t('brain.deleteListNamed', { title: list.title })} onClick={onDelete}>
          <Trash2 size={18} aria-hidden="true" />
        </button>
      </div>
      {items.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} className="stack">
          {items.map((i) => (
            <ListItemRow key={i.id} item={i} onRemove={() => void removeItems([i.id])} />
          ))}
        </ul>
      )}
      <form
        className="row"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!text.trim()) return;
          await addListItems(list.id, [text], all);
          setText('');
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label={t('brain.addItemTo', { title: list.title })}>
            {(p) => <input {...p} className="input" maxLength={200} value={text} onChange={(e) => setText(e.target.value)} />}
          </Field>
        </div>
        <button type="submit" className="btn" style={{ alignSelf: 'flex-end' }} disabled={!text.trim()}>
          {t('brain.add')}
        </button>
      </form>
      {done.length > 0 && (
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => void removeItems(done.map((i) => i.id))}>
          {t('brain.clearDone')}
        </button>
      )}
    </section>
  );
}

/** Casilla con estado optimista: responde al instante y se alinea con lo guardado al llegar. */
function ListItemRow({ item, onRemove }: { item: ListItemRec; onRemove: () => void }) {
  const { t } = useI18n();
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  if (optimistic !== null && optimistic === item.done) setOptimistic(null);
  const done = optimistic ?? item.done;
  return (
    <li className="row">
      <label className="row" style={{ flex: 1, minWidth: 0 }}>
        <input
          type="checkbox"
          checked={done}
          onChange={(e) => {
            setOptimistic(e.target.checked);
            void setItemDone(item.id, e.target.checked).catch(() => setOptimistic(null));
          }}
        />
        <span style={{ textDecoration: done ? 'line-through' : undefined, overflowWrap: 'anywhere' }}>{item.text}</span>
      </label>
      <button type="button" className="icon-btn" aria-label={t('brain.removeItemNamed', { text: item.text })} onClick={onRemove}>
        <X size={16} aria-hidden="true" />
      </button>
    </li>
  );
}
